<template>
  <div class="telegram-integration-layout">
    <Sidebar />

    <div class="main-content">
      <TopHeader />

      <main class="page-content">

        <div class="page-header">
          <div class="page-header-left">
            <div class="page-icon">
              <img :src="telegramLogo" alt="Telegram" width="48" height="48" style="border-radius:50%;display:block;" />
            </div>
            <div>
              <h1 class="page-title">Telegram Integration</h1>
              <p class="page-subtitle">Connect your Telegram bots to receive and reply to conversations from the Quali-D dashboard.</p>
            </div>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Loading connected bots...</p>
        </div>

        <div v-else class="connections-section">

          <!-- Info banner -->
          <div class="info-banner">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0;margin-top:2px">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <div>
              <strong>How it works</strong>
              <p>
                Create a bot on Telegram via <strong>@BotFather</strong> (send <code>/newbot</code>),
                copy the token it gives you, and paste it below. Quali-D will register the webhook automatically.
              </p>
            </div>
          </div>

          <!-- Connect new bot form -->
          <div class="section-card">
            <div class="section-card-header">
              <h2>Connected Bots</h2>
              <button @click="showConnectForm = !showConnectForm" class="btn-connect" :disabled="connecting">
                <img :src="telegramLogo" alt="" width="18" height="18" style="border-radius:50%" />
                Connect Telegram Bot
              </button>
            </div>

            <!-- Connect form -->
            <div v-if="showConnectForm" class="connect-form">
              <div class="form-group">
                <label class="form-label">Bot Token <span class="label-hint">(from @BotFather)</span></label>
                <input
                  v-model="newBotToken"
                  type="text"
                  class="form-input"
                  placeholder="e.g. 1234567890:AABBccDDeeFFggHH..."
                  :disabled="connecting"
                />
              </div>
              <div class="form-actions">
                <button @click="connectBot" class="btn-primary" :disabled="connecting || !newBotToken.trim()">
                  <span v-if="connecting" class="spinner-sm"></span>
                  {{ connecting ? 'Connecting...' : 'Connect Bot' }}
                </button>
                <button @click="showConnectForm = false; newBotToken = ''" class="btn-secondary">Cancel</button>
              </div>
              <div v-if="connectError" class="form-error">{{ connectError }}</div>
            </div>

            <!-- Empty state -->
            <div v-if="bots.length === 0 && !showConnectForm" class="empty-state">
              <div class="empty-icon">
                <img :src="telegramLogo" alt="Telegram" width="64" height="64" style="border-radius:50%" />
              </div>
              <h3>No Telegram bots connected yet</h3>
              <p>Click <strong>Connect Telegram Bot</strong> above, paste your BotFather token, and you're ready.</p>
            </div>

            <!-- Bots list -->
            <div v-else-if="bots.length > 0" class="pages-list">
              <div v-for="bot in bots" :key="bot.id" class="bot-row">
                <div class="page-item">
                  <div class="page-avatar">
                    <img :src="telegramLogo" alt="Telegram" width="44" height="44" style="border-radius:50%;display:block;" />
                  </div>
                  <div class="page-info">
                    <div class="page-name">@{{ bot.bot_username }}</div>
                    <div v-if="bot.bot_first_name" class="page-category">{{ bot.bot_first_name }}</div>
                    <div class="page-meta">
                      <span class="page-id">Bot ID: {{ bot.bot_id }}</span>
                      <span class="status-badge" :class="bot.is_active === 1 ? 'status-active' : 'status-inactive'">
                        {{ bot.is_active === 1 ? 'Active' : 'Inactive' }}
                      </span>
                      <span v-if="bot.force_contact" class="status-badge status-force-contact">📱 Contact required</span>
                      <span v-if="bot.bot_mode === 'restaurant'" class="status-badge status-restaurant-mode">🍽️ Restaurant</span>
                    </div>
                    <div class="page-webhook">
                      <span class="webhook-label">Webhook:</span>
                      <code class="webhook-url">{{ bot.webhook_url }}</code>
                    </div>
                  </div>
                  <div class="page-actions">
                    <div v-if="bot.connected_at" class="page-date">
                      Connected {{ formatDate(bot.connected_at) }}
                    </div>
                    <button
                      @click="toggleSettings(bot)"
                      class="btn-settings"
                      :class="{ 'btn-settings-active': settingsBotId === bot.id }"
                      :disabled="disconnecting === bot.id"
                    >
                      ⚙ Settings
                    </button>
                    <button
                      @click="toggleEditProfile(bot)"
                      class="btn-edit-profile"
                      :disabled="disconnecting === bot.id"
                    >
                      Edit Profile
                    </button>
                    <button
                      @click="disconnectBot(bot)"
                      class="btn-disconnect"
                      :disabled="disconnecting === bot.id"
                    >
                      {{ disconnecting === bot.id ? 'Disconnecting...' : 'Disconnect' }}
                    </button>
                  </div>
                </div>

                <!-- Settings Panel -->
                <div v-if="settingsBotId === bot.id && settingsForms[bot.id]" class="settings-panel">
                  <div class="settings-panel-title">Bot Settings</div>
                  <div class="setting-row">
                    <div class="setting-row-info">
                      <span class="setting-row-label">Bot Mode</span>
                      <span class="setting-row-desc">Choose how this bot operates</span>
                    </div>
                    <div class="mode-radio-group">
                      <div class="mode-radio-option" :class="{ active: settingsForms[bot.id].botMode === 'standard' }"
                           @click="selectBotMode(bot, 'standard')">
                        <span class="mode-radio-label">💬 Standard</span>
                        <span class="mode-radio-desc">Agents handle conversations</span>
                      </div>
                      <div class="mode-radio-option" :class="{ active: settingsForms[bot.id].botMode === 'restaurant' }"
                           @click="selectBotMode(bot, 'restaurant')">
                        <span class="mode-radio-label">🍽️ Restaurant</span>
                        <span class="mode-radio-desc">Mini App ordering</span>
                      </div>
                    </div>
                  </div>
                  <hr class="settings-divider" />
                  <div class="setting-row">
                    <div class="setting-row-info">
                      <span class="setting-row-label">Force Contact Sharing</span>
                      <span class="setting-row-desc">Require customers to share their phone number before chatting</span>
                    </div>
                    <label class="toggle-switch">
                      <input type="checkbox" v-model="settingsForms[bot.id].forceContact" />
                      <span class="toggle-track"></span>
                    </label>
                  </div>
                  <transition name="fade">
                    <div v-if="settingsForms[bot.id].forceContact" class="setting-message-row">
                      <label class="form-label">Message sent to customers without a phone number</label>
                      <textarea
                        v-model="settingsForms[bot.id].forceContactMessage"
                        class="form-textarea"
                        rows="3"
                        maxlength="500"
                        placeholder="Please share your phone number to continue the conversation."
                        style="max-width:100%"
                      ></textarea>
                      <span class="setting-hint">A "Share Phone Number" button is automatically attached. Leave blank to use the default message.</span>
                    </div>
                  </transition>
                  <div class="settings-actions">
                    <button @click="saveSettings(bot)" class="btn-primary" :disabled="savingSettings === bot.id">
                      <span v-if="savingSettings === bot.id" class="spinner-sm"></span>
                      {{ savingSettings === bot.id ? 'Saving...' : 'Save Settings' }}
                    </button>
                    <button @click="settingsBotId = null" class="btn-secondary">Cancel</button>
                    <span v-if="settingsSaveError[bot.id]" class="profile-error">{{ settingsSaveError[bot.id] }}</span>
                    <span v-if="settingsSaveSuccess[bot.id]" class="profile-success">Saved!</span>
                  </div>
                </div>

                <!-- Edit Profile Panel -->
                <div v-if="editingProfileBotId === bot.id" class="profile-panel">
                  <div v-if="profileLoading === bot.id" class="profile-loading">
                    <div class="spinner-sm" style="border-top-color:#229ED9;border-color:rgba(34,158,217,.2);width:18px;height:18px;border-width:2px"></div>
                    <span>Loading profile...</span>
                  </div>
                  <div v-else class="profile-form">
                    <div class="profile-pic-note">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink:0;margin-top:1px">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                      <span>To change the <strong>profile picture</strong>, open <strong>@BotFather</strong> on Telegram and send <code>/setuserpic</code>.</span>
                    </div>
                    <div class="profile-fields">
                      <div class="form-group">
                        <label class="form-label">Bot Name</label>
                        <input
                          v-model="profileForms[bot.id].name"
                          type="text"
                          class="form-input"
                          placeholder="Display name shown to users"
                          maxlength="64"
                        />
                      </div>
                      <div class="form-group">
                        <label class="form-label">Description <span class="label-hint">(shown on bot profile page)</span></label>
                        <textarea
                          v-model="profileForms[bot.id].description"
                          class="form-textarea"
                          placeholder="What can this bot do? Shown when users open the bot profile."
                          maxlength="512"
                          rows="3"
                        ></textarea>
                      </div>
                      <div class="form-group">
                        <label class="form-label">Short Description <span class="label-hint">(shown on start screen)</span></label>
                        <input
                          v-model="profileForms[bot.id].shortDescription"
                          type="text"
                          class="form-input"
                          placeholder="Brief intro shown when users start the bot"
                          maxlength="120"
                        />
                      </div>
                    </div>
                    <div class="profile-actions">
                      <button
                        @click="saveProfile(bot)"
                        class="btn-primary"
                        :disabled="savingProfile === bot.id"
                      >
                        <span v-if="savingProfile === bot.id" class="spinner-sm"></span>
                        {{ savingProfile === bot.id ? 'Saving...' : 'Save Profile' }}
                      </button>
                      <button @click="editingProfileBotId = null" class="btn-secondary">Cancel</button>
                      <span v-if="profileSaveError[bot.id]" class="profile-error">{{ profileSaveError[bot.id] }}</span>
                      <span v-if="profileSaveSuccess[bot.id]" class="profile-success">Saved!</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Confirm disconnect modal -->
    <div v-if="confirmDisconnect" class="modal-overlay" @click.self="confirmDisconnect = null">
      <div class="modal-box">
        <h3>Disconnect Bot?</h3>
        <p>
          Disconnecting <strong>@{{ confirmDisconnect.bot_username }}</strong> will stop receiving messages.
          Existing conversation history is preserved.
        </p>
        <div class="modal-actions">
          <button @click="doDisconnect" class="btn-danger" :disabled="disconnecting">
            {{ disconnecting ? 'Disconnecting...' : 'Yes, Disconnect' }}
          </button>
          <button @click="confirmDisconnect = null" class="btn-secondary">Cancel</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { DashboardService } from '@/services/dashboard'
import telegramLogo from '@/assets/telegram-logo.svg'

export default {
  name: 'TelegramIntegrationView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      telegramLogo,
      loading: true,
      bots: [],
      showConnectForm: false,
      newBotToken: '',
      connecting: false,
      connectError: '',
      disconnecting: null,
      confirmDisconnect: null,
      // Profile editing
      editingProfileBotId: null,
      profileLoading: null,
      savingProfile: null,
      profileForms: {},
      profileSaveError: {},
      profileSaveSuccess: {},

      // Bot settings
      settingsBotId: null,
      savingSettings: null,
      settingsForms: {},
      settingsSaveError: {},
      settingsSaveSuccess: {}
    }
  },

  async mounted () {
    await this.loadBots()
  },

  methods: {
    async loadBots () {
      this.loading = true
      try {
        const res = await DashboardService.getTelegramBots()
        if (res.success) {
          this.bots = res.bots || []
        }
      } catch (err) {
        console.error('Failed to load Telegram bots:', err)
      } finally {
        this.loading = false
      }
    },

    async connectBot () {
      if (!this.newBotToken.trim()) return
      this.connecting = true
      this.connectError = ''
      try {
        const res = await DashboardService.connectTelegramBot(this.newBotToken.trim())
        if (res.success) {
          this.showConnectForm = false
          this.newBotToken = ''
          await this.loadBots()
        } else {
          this.connectError = res.message || 'Failed to connect bot'
        }
      } catch (err) {
        this.connectError = err.response?.data?.message || 'Failed to connect bot. Check the token and try again.'
      } finally {
        this.connecting = false
      }
    },

    disconnectBot (bot) {
      this.confirmDisconnect = bot
    },

    async doDisconnect () {
      const bot = this.confirmDisconnect
      if (!bot) return
      this.disconnecting = bot.id
      try {
        const res = await DashboardService.disconnectTelegramBot(bot.id)
        if (res.success) {
          this.bots = this.bots.filter(b => b.id !== bot.id)
          this.confirmDisconnect = null
        }
      } catch (err) {
        console.error('Failed to disconnect bot:', err)
      } finally {
        this.disconnecting = null
      }
    },

    async toggleEditProfile (bot) {
      if (this.editingProfileBotId === bot.id) {
        this.editingProfileBotId = null
        return
      }
      this.editingProfileBotId = bot.id
      this.profileLoading = bot.id
      this.profileSaveError[bot.id] = ''
      this.profileSaveSuccess[bot.id] = false
      try {
        const res = await DashboardService.getTelegramBotProfile(bot.id)
        if (res.success) {
          this.profileForms[bot.id] = {
            name: res.name || '',
            description: res.description || '',
            shortDescription: res.short_description || ''
          }
        } else {
          this.profileForms[bot.id] = { name: '', description: '', shortDescription: '' }
        }
      } catch (_) {
        this.profileForms[bot.id] = { name: '', description: '', shortDescription: '' }
      } finally {
        this.profileLoading = null
      }
    },

    async saveProfile (bot) {
      const form = this.profileForms[bot.id]
      if (!form) return
      this.savingProfile = bot.id
      this.profileSaveError[bot.id] = ''
      this.profileSaveSuccess[bot.id] = false
      try {
        const res = await DashboardService.updateTelegramBotProfile(bot.id, {
          name: form.name,
          description: form.description,
          shortDescription: form.shortDescription
        })
        if (res.success) {
          this.profileSaveSuccess[bot.id] = true
          // Update display name in the local list
          const idx = this.bots.findIndex(b => b.id === bot.id)
          if (idx !== -1) this.bots[idx].bot_first_name = form.name
          setTimeout(() => { this.profileSaveSuccess[bot.id] = false }, 3000)
        } else {
          this.profileSaveError[bot.id] = res.message || 'Failed to save profile'
        }
      } catch (err) {
        this.profileSaveError[bot.id] = 'Failed to save profile'
      } finally {
        this.savingProfile = null
      }
    },

    toggleSettings (bot) {
      if (this.settingsBotId === bot.id) {
        this.settingsBotId = null
        return
      }
      this.settingsBotId = bot.id
      this.settingsForms[bot.id] = {
        forceContact: !!bot.force_contact,
        forceContactMessage: bot.force_contact_message || '',
        botMode: bot.bot_mode || 'standard'
      }
      this.settingsSaveError[bot.id] = ''
      this.settingsSaveSuccess[bot.id] = false
    },

    async selectBotMode (bot, mode) {
      if (!this.settingsForms[bot.id]) return
      this.settingsForms[bot.id].botMode = mode
      await this.saveSettings(bot)
    },

    async saveSettings (bot) {
      const form = this.settingsForms[bot.id]
      if (!form) return
      this.savingSettings = bot.id
      this.settingsSaveError[bot.id] = ''
      this.settingsSaveSuccess[bot.id] = false
      try {
        const res = await DashboardService.saveTelegramBotSettings(bot.id, {
          forceContact: form.forceContact,
          forceContactMessage: form.forceContactMessage,
          botMode: form.botMode
        })
        if (res.success) {
          this.settingsSaveSuccess[bot.id] = true
          const idx = this.bots.findIndex(b => b.id === bot.id)
          if (idx !== -1) {
            this.bots[idx].force_contact = form.forceContact ? 1 : 0
            this.bots[idx].force_contact_message = form.forceContactMessage || null
            this.bots[idx].bot_mode = form.botMode
          }
          setTimeout(() => { this.settingsSaveSuccess[bot.id] = false }, 3000)
        } else {
          this.settingsSaveError[bot.id] = res.message || 'Failed to save settings'
        }
      } catch (_) {
        this.settingsSaveError[bot.id] = 'Failed to save settings'
      } finally {
        this.savingSettings = null
      }
    },

    formatDate (dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    }
  }
}
</script>

<style scoped>
/* ─── Layout ─────────────────────────────────────────────── */
.telegram-integration-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary, #f5f6fa);
}
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.page-content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

/* ─── Page header ─────────────────────────────────────────── */
.page-header { margin-bottom: 24px; }
.page-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}
.page-icon {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
}
.page-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary, #1a1a2e);
  margin: 0 0 4px;
}
.page-subtitle {
  font-size: 14px;
  color: var(--text-secondary, #666);
  margin: 0;
}

/* ─── Loading ─────────────────────────────────────────────── */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px;
  gap: 12px;
  color: var(--text-secondary, #888);
}
.spinner {
  width: 28px; height: 28px;
  border: 3px solid var(--border-primary, #e0e0e0);
  border-top-color: #229ED9;
  border-radius: 50%;
  animation: spin .7s linear infinite;
}
.spinner-sm {
  display: inline-block;
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,.4);
  border-top-color: white;
  border-radius: 50%;
  animation: spin .7s linear infinite;
  margin-right: 6px;
  vertical-align: middle;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ─── Info banner ─────────────────────────────────────────── */
.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #e8f4fd;
  border: 1px solid #bee3f8;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 20px;
  font-size: 14px;
  color: #1a5276;
}
.info-banner strong { display: block; margin-bottom: 4px; }
.info-banner p { margin: 0; }
.info-banner code {
  background: rgba(34,158,217,.15);
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 13px;
}

/* ─── Section card ────────────────────────────────────────── */
.section-card {
  background: var(--bg-primary, white);
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0,0,0,.08);
  overflow: hidden;
  border: 1px solid var(--border-primary, transparent);
}
.section-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-primary, #f0f0f0);
}
.section-card-header h2 {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary, #1a1a2e);
  margin: 0;
}

/* ─── Buttons ─────────────────────────────────────────────── */
.btn-connect {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #229ED9;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background .2s;
}
.btn-connect:hover:not(:disabled) { background: #1a8dbf; }
.btn-connect:disabled { opacity: .6; cursor: not-allowed; }

.btn-primary {
  display: inline-flex;
  align-items: center;
  background: #229ED9;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 9px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background .2s;
}
.btn-primary:hover:not(:disabled) { background: #1a8dbf; }
.btn-primary:disabled { opacity: .6; cursor: not-allowed; }

.btn-secondary {
  background: transparent;
  color: var(--text-secondary, #555);
  border: 1px solid var(--border-primary, #d0d5dd);
  border-radius: 8px;
  padding: 9px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: background .2s;
}
.btn-secondary:hover { background: var(--bg-tertiary, #f5f5f5); }

.btn-danger {
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 9px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background .2s;
}
.btn-danger:hover:not(:disabled) { background: #b91c1c; }
.btn-danger:disabled { opacity: .6; cursor: not-allowed; }

/* ─── Connect form ────────────────────────────────────────── */
.connect-form {
  padding: 20px;
  border-bottom: 1px solid var(--border-primary, #f0f0f0);
  background: var(--bg-secondary, #fafbfc);
}
.form-group { margin-bottom: 14px; }
.form-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary, #444);
  margin-bottom: 6px;
}
.label-hint { font-weight: 400; color: var(--text-secondary, #888); }
.form-input {
  width: 100%;
  max-width: 480px;
  padding: 9px 12px;
  border: 1px solid var(--border-primary, #d0d5dd);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary, #1a1a2e);
  background: var(--bg-primary, white);
  outline: none;
  transition: border-color .2s;
  box-sizing: border-box;
}
.form-input:focus { border-color: #229ED9; }
.form-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}
.form-error {
  margin-top: 10px;
  font-size: 13px;
  color: #dc2626;
}

/* ─── Empty state ─────────────────────────────────────────── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  text-align: center;
  color: var(--text-secondary, #888);
}
.empty-icon { margin-bottom: 16px; opacity: .85; }
.empty-state h3 { font-size: 16px; color: var(--text-primary, #444); margin: 0 0 8px; }
.empty-state p { font-size: 14px; margin: 0; max-width: 360px; }

/* ─── Bots list ───────────────────────────────────────────── */
.pages-list { padding: 8px 0; }
.bot-row { border-bottom: 1px solid var(--border-primary, #f5f5f5); }
.bot-row:last-child { border-bottom: none; }
.page-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 20px;
  transition: background .15s;
}
.page-item:hover { background: var(--bg-secondary, #fafbfc); }

.page-avatar {
  width: 44px; height: 44px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
}

.page-info { flex: 1; min-width: 0; }
.page-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary, #1a1a2e);
  margin-bottom: 2px;
}
.page-category {
  font-size: 13px;
  color: var(--text-secondary, #888);
  margin-bottom: 4px;
}
.page-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}
.page-id { font-size: 12px; color: var(--text-secondary, #aaa); }
.page-webhook {
  font-size: 12px;
  color: var(--text-secondary, #888);
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.webhook-label { font-weight: 500; }
.webhook-url {
  background: var(--bg-tertiary, #f0f0f0);
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  color: var(--text-secondary, #555);
  word-break: break-all;
}

.status-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 99px;
}
.status-active { background: #d1fae5; color: #065f46; }
.status-inactive { background: #fee2e2; color: #991b1b; }

.page-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
}
.page-date { font-size: 12px; color: var(--text-secondary, #aaa); }
.btn-disconnect {
  background: transparent;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 13px;
  cursor: pointer;
  transition: all .2s;
}
.btn-disconnect:hover:not(:disabled) { background: #fef2f2; }
.btn-disconnect:disabled { opacity: .6; cursor: not-allowed; }

/* ─── Modal ───────────────────────────────────────────────── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-box {
  background: var(--bg-primary, white);
  border-radius: 12px;
  padding: 28px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0,0,0,.25);
  border: 1px solid var(--border-primary, transparent);
}
.modal-box h3 { margin: 0 0 10px; font-size: 17px; color: var(--text-primary, #1a1a2e); }
.modal-box p { font-size: 14px; color: var(--text-secondary, #555); margin: 0 0 20px; }
.modal-actions { display: flex; gap: 10px; }

/* ─── Edit Profile button ─────────────────────────────────── */
.btn-edit-profile {
  background: transparent;
  color: #229ED9;
  border: 1px solid #bee3f8;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 13px;
  cursor: pointer;
  transition: all .2s;
}
.btn-edit-profile:hover:not(:disabled) { background: #e8f4fd; }
.btn-edit-profile:disabled { opacity: .6; cursor: not-allowed; }

/* ─── Profile panel ───────────────────────────────────────── */
.profile-panel {
  border-top: 1px solid var(--border-primary, #f0f0f0);
  background: var(--bg-secondary, #fafbfc);
  padding: 20px 20px 20px 80px;
}
.profile-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text-secondary, #888);
}
.profile-pic-note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
  color: #795548;
  margin-bottom: 16px;
}
.profile-pic-note code {
  background: rgba(121, 85, 72, .12);
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 12px;
}
.profile-fields { display: flex; flex-direction: column; gap: 0; }
.form-textarea {
  width: 100%;
  max-width: 480px;
  padding: 9px 12px;
  border: 1px solid var(--border-primary, #d0d5dd);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary, #1a1a2e);
  background: var(--bg-primary, white);
  outline: none;
  resize: vertical;
  transition: border-color .2s;
  box-sizing: border-box;
  font-family: inherit;
}
.form-textarea:focus { border-color: #229ED9; }
.profile-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.profile-error { font-size: 13px; color: #dc2626; }
.profile-success { font-size: 13px; color: #059669; font-weight: 500; }

/* ─── Force contact pill ──────────────────────────────────── */
.status-force-contact {
  background: #ede9fe;
  color: #5b21b6;
}

/* ─── Restaurant mode pill ────────────────────────────────── */
.status-restaurant-mode {
  background: #fef3c7;
  color: #92400e;
}

/* ─── Settings divider ────────────────────────────────────── */
.settings-divider {
  border: none;
  border-top: 1px solid var(--border-primary, #f0f0f0);
  margin: 12px 0;
}

/* ─── Mode radio group ────────────────────────────────────── */
.mode-radio-group {
  display: flex;
  gap: 8px;
}
.mode-radio-option {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 8px 14px;
  border: 1.5px solid var(--border-primary, #e0e0e0);
  border-radius: 8px;
  cursor: pointer;
  transition: border-color .2s, background .2s;
  min-width: 110px;
}
.mode-radio-option:hover {
  border-color: #229ED9;
  background: rgba(34, 158, 217, 0.08);
}
.mode-radio-option.active {
  border-color: #229ED9;
  background: rgba(34, 158, 217, 0.15);
}
.mode-radio-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary, #1a1a1a);
}
.mode-radio-desc {
  font-size: 11px;
  color: var(--text-secondary, #888);
}

/* ─── Settings button ─────────────────────────────────────── */
.btn-settings {
  background: transparent;
  color: var(--text-secondary, #666);
  border: 1px solid var(--border-primary, #d0d5dd);
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 13px;
  cursor: pointer;
  transition: all .2s;
}
.btn-settings:hover:not(:disabled) {
  background: var(--bg-secondary, #f5f5f5);
  border-color: #229ED9;
  color: #229ED9;
}
.btn-settings.btn-settings-active {
  background: rgba(34,158,217,.1);
  border-color: #229ED9;
  color: #229ED9;
}
.btn-settings:disabled { opacity: .6; cursor: not-allowed; }

/* ─── Settings panel ──────────────────────────────────────── */
.settings-panel {
  border-top: 1px solid var(--border-primary, #f0f0f0);
  background: var(--bg-secondary, #fafbfc);
  padding: 20px 20px 20px 80px;
}
.settings-panel-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--text-secondary, #888);
  text-transform: uppercase;
  letter-spacing: .6px;
  margin-bottom: 18px;
}
.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}
.setting-row-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.setting-row-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary, #1a1a2e);
}
.setting-row-desc {
  font-size: 12px;
  color: var(--text-secondary, #888);
}
.setting-message-row { margin-bottom: 14px; }
.setting-hint {
  display: block;
  font-size: 12px;
  color: var(--text-secondary, #aaa);
  margin-top: 6px;
  font-style: italic;
}
.settings-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 6px;
}

/* ─── Toggle switch ───────────────────────────────────────── */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}
.toggle-switch input { opacity: 0; width: 0; height: 0; position: absolute; }
.toggle-track {
  position: absolute;
  inset: 0;
  background: #d1d5db;
  border-radius: 24px;
  cursor: pointer;
  transition: background .2s;
}
.toggle-track::after {
  content: '';
  position: absolute;
  left: 3px;
  top: 3px;
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  transition: transform .2s;
  box-shadow: 0 1px 3px rgba(0,0,0,.2);
}
.toggle-switch input:checked + .toggle-track { background: #229ED9; }
.toggle-switch input:checked + .toggle-track::after { transform: translateX(20px); }

/* ─── Fade transition ─────────────────────────────────────── */
.fade-enter-active, .fade-leave-active {
  transition: opacity .2s ease, transform .2s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* ─── Dark mode overrides ─────────────────────────────────── */
:global(.dark) .info-banner {
  background: rgba(34, 158, 217, .12);
  border-color: rgba(34, 158, 217, .3);
  color: #90cdf4;
}
:global(.dark) .info-banner code {
  background: rgba(34, 158, 217, .2);
  color: #bee3f8;
}
:global(.dark) .section-card {
  box-shadow: 0 1px 4px rgba(0,0,0,.3);
}
:global(.dark) .status-active {
  background: rgba(6, 95, 70, .35);
  color: #6ee7b7;
}
:global(.dark) .status-inactive {
  background: rgba(153, 27, 27, .35);
  color: #fca5a5;
}
:global(.dark) .btn-disconnect {
  border-color: rgba(220, 38, 38, .4);
  color: #fca5a5;
}
:global(.dark) .btn-disconnect:hover:not(:disabled) {
  background: rgba(220, 38, 38, .12);
}
:global(.dark) .btn-secondary:hover {
  background: var(--bg-tertiary);
}
:global(.dark) .form-error { color: #fca5a5; }
:global(.dark) .btn-edit-profile {
  border-color: rgba(34, 158, 217, .35);
  color: #63b3ed;
}
:global(.dark) .btn-edit-profile:hover:not(:disabled) {
  background: rgba(34, 158, 217, .1);
}
:global(.dark) .profile-panel {
  border-top-color: var(--border-primary);
}
:global(.dark) .profile-pic-note {
  background: rgba(255, 193, 7, .08);
  border-color: rgba(255, 193, 7, .3);
  color: #f6d860;
}
:global(.dark) .profile-pic-note code {
  background: rgba(255, 193, 7, .15);
}
:global(.dark) .profile-error { color: #fca5a5; }
:global(.dark) .profile-success { color: #6ee7b7; }
:global(.dark) .status-force-contact {
  background: rgba(91, 33, 182, .25);
  color: #c4b5fd;
}
:global(.dark) .status-restaurant-mode {
  background: rgba(146, 64, 14, .25);
  color: #fcd34d;
}
:global(.dark) .mode-radio-option {
  border-color: var(--border-primary, #374151);
}
:global(.dark) .settings-panel { border-top-color: var(--border-primary); }
:global(.dark) .toggle-track { background: #4b5563; }
:global(.dark) .btn-settings { color: var(--text-secondary, #aaa); }
:global(.dark) .btn-settings:hover:not(:disabled),
:global(.dark) .btn-settings.btn-settings-active {
  background: rgba(34,158,217,.12);
  border-color: #229ED9;
  color: #63b3ed;
}
</style>
